import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import SiteComponent from 'yoda-site-components/lib/components/SiteComponent/SiteComponent';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import analyticsAction from 'yoda-site-components/lib/actions/AnalyticsAction';
import RecommendationSwitch from 'yoda-site-components/lib/components/RecommendationSwitch';
import get from 'lodash/get';
import selectProductType from 'yoda-site-components/lib/selectors/productDetails/selectProductType';
import { selectProductReviewID } from 'yoda-site-components/lib/selectors/ProductDetailsSelector';
import { selectPreferences } from 'yoda-site-components/lib/selectors/ContextSelector';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import BelowTheFold from '../BelowTheFold/BelowTheFold';
import * as styles from './ProductInfo.css';
import CreditCardBanner from '../CreditCardBanner/CreditCardBanner';
import RenderBazaarVoice from '../BazaarVoice/RenderBazaarVoice';
import ProductInfoSelector, {
    getProductMattressSpec,
} from '../../selectors/ProductInfo/ProductInfoSelector';
import BazaarVoice from '../BazaarVoice/BazaarVoiceLazy';

const cx = classNames.bind(styles);

export class ProductInfoDesktop extends SiteComponent {
    static getDerivedStateFromProps(props, state) {
        const { pageLoadComplete } = props;
        if (pageLoadComplete && !state.pageLoadComplete) {
            return { pageLoadComplete };
        }
        return null;
    }

    static propTypes = {
        dataAutomationId: PropTypes.string,
        productDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
        selectProductType: PropTypes.oneOfType([PropTypes.object]),
        pTmplType: PropTypes.string,
        productReviewID: PropTypes.string,
        isReviewClicked: PropTypes.bool,
        pageLoadComplete: PropTypes.bool,
    };

    static defaultProps = {
        dataAutomationId: '',
        productDetails: {},
        selectProductType: {},
        pTmplType: '',
        productReviewID: '',
        isReviewClicked: false,
        pageLoadComplete: false,
    };

    /* istanbul ignore next */
    constructor(props) {
        super(props);
        this.state = {
            activeNode: 'prodDesc',
            pageLoadComplete: get(props, 'pageLoadComplete', false),
        };
    }

    /* istanbul ignore next */
    componentDidMount() {
        const {
            featureFlags: { enableShowReviewsButtonOnReviewSection = false },
        } = this.props;
        if (enableShowReviewsButtonOnReviewSection) {
            const reviewSectionButton = document.querySelector(`#accordion-button-0`);
            setTimeout(() => {
                reviewSectionButton?.click();
            }, 500);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.state.pageLoadComplete && !prevProps.pageLoadComplete) {
            const isFromGallery = LocalStorage.getData('fromGallery') || {};
            /* istanbul ignore else */
            if (isFromGallery) {
                /* istanbul ignore else */
                if (isFromGallery.scrollTo === 'reviews') {
                    this.toggleProdInfoSelection('BVSummaryReviewBtn', 'custReview');
                } else if (isFromGallery.scrollTo === 'features') {
                    this.toggleProdInfoSelection('prodDescNode', 'prodDesc');
                }
            }
        }
    }

    toggleProdInfoSelection(node, type) {
        const {
            featureFlags: { enableShopTheLook = true },
        } = this.props;
        /* istanbul ignore next */
        const isShopTheLookLoaded = () => document.getElementsByClassName('stylitics-classic-base');
        this.setState({ activeNode: type });
        if (type === 'custReview') {
            const scrollToElem = document.querySelector(`#${node}`);
            if ((enableShopTheLook && isShopTheLookLoaded) || !enableShopTheLook) {
                setTimeout(() => {
                    scrollToElem?.click();
                }, 500);
            }
            LocalStorage.removeData('fromGallery');
        } else if (this[node] && this[node].scrollIntoView) {
            /* istanbul ignore next */
            this[node].scrollIntoView({ behavior: 'smooth' });
        }
    }

    /* CoreMedia CMFallback removed */

    desktopRenderer = () => {
        const {
            featureFlags: {
                enableDesktopAccordionBV = true,
                bazaarVoice = true,
                enableIrisCreditCardBanner,
                customerPhotos = true,
            },
            pageType,
            productDetails,
            productReviewID,
            preferences,
        } = this.props;
        const recSlots = preferences?.recommendations?.slots || [];
        // Render ONLY slots explicitly tagged for the productInfo zone.
        // `recommendations.slots` merges this page's slots with shared/common slots
        // injected into every microsite; without a strict tag match those common slots
        // (oos/addtocart/rewards) would leak in. So require `zone === 'productInfo'` and
        // never fall back to "render all".
        const productInfoSlots = recSlots.filter((s) => s.zone === 'productInfo');
        const breadcrumbs = get(productDetails, 'breadCrumbInfo.breadcrumbs', []);
        let pdpDivision = '';

        /* istanbul ignore next */
        if (breadcrumbs.length) {
            pdpDivision = get(breadcrumbs, '[1].breadCrumbLabel', '');
        }

        let renderBVSegment = null;

        if (!enableDesktopAccordionBV) {
            if (bazaarVoice) {
                renderBVSegment = (
                    <>
                        <div
                            ref={(node) => {
                                this.custReviewNode = node;
                            }}
                            className={cx('lg12', 'xl12', 'block', 'noBorder')}
                            aria-label="CustomerReview"
                            id="reviewSectionId"
                        >
                            <div
                                data-bv-show="review_highlights"
                                data-bv-product-id={productReviewID}
                            />
                            <div data-bv-show="reviews" data-bv-product-id={productReviewID} />
                            <BazaarVoice
                                containerId="BVReviewsContainer"
                                submissionContainerId="BVSubmissionContainer"
                                contentType="reviews"
                            />
                        </div>
                        <div
                            ref={(node) => {
                                this.quesAndAnsNode = node;
                            }}
                            className={cx('lg12', 'xl12', 'block')}
                            aria-label="questionAndAnswer"
                            id="QASectionId"
                        >
                            <div data-bv-show="questions" data-bv-product-id={productReviewID} />

                            <BazaarVoice
                                containerId="BVQuestionsContainer"
                                submissionContainerId="BVQASubmissionContainer"
                                contentType="qa"
                            />
                        </div>
                    </>
                );
            }
        } else {
            renderBVSegment = <RenderBazaarVoice mobileTabletLogic={false} />;
        }

        return (
            <BelowTheFold>
                {renderBVSegment}
                {productInfoSlots.length > 0 ? (
                    <section className={cx('section')}>
                        <div className={cx('certona-row')}>
                            {productInfoSlots.map(slot => (
                                <RecommendationSwitch key={slot.slotId} strategy={slot.slotId} pageType="PDP" itemId={productDetails.id} />
                            ))}
                        </div>
                    </section>
                ) : null}
                <div className={cx('rowBlock')} aria-label="productInfo-section">
                    {enableIrisCreditCardBanner ? (
                        <section className={cx('section')}>
                            <CreditCardBanner analyticsAction={this.props.analyticsAction} />
                        </section>
                    ) : null}

                    {customerPhotos ? (
                        <BazaarVoice containerId="bv-gallery" showLoader={false} />
                    ) : null}
                </div>
            </BelowTheFold>
        );
    };

    render() {
        return this.desktopRenderer();
    }
}

const mapStateToProps = (state) => ({
    deviceType: state.context ? state.context.deviceType : {},
    featureFlags: state.context ? state.context.featureFlags : {},
    infoDetails: ProductInfoSelector(state),
    selectProductType: selectProductType(false)(state),
    productReviewID: selectProductReviewID(state),
    preferences: selectPreferences(state),
    isMattressSpec: getProductMattressSpec(state),
    pageLoadComplete: state?.Analytics?.pageReadyCmpleted || false,
});

const mapDispatchToProps = (dispatch) => ({
    analyticsAction: bindActionCreators(analyticsAction, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductInfoDesktop);
